import './lib/host-flag'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { OmniAuthProvider } from '@omni/auth'
import { ToastProvider } from '@omni/ui'
import App from './App'
import { instance, shellProject } from './lib/config'
import { applyInstanceTheme } from './lib/instance-theme'
import './index.css'

// Brand the document from the selected instance before first paint. For the
// default enterprise-dna instance this is a no-op (theme matches tokens).
applyInstanceTheme(instance)

// staleTime/refetchOnWindowFocus match what five of the seven surfaces use in
// their standalone bootstraps; one client serves the whole shell.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
})

// One session (§8.3): the same project in all three slots makes @omni/auth
// collapse to a single Supabase client under one storage key — one magic
// link, one session, no triple OTP fan-out.
//
// Schema-per-segment (Layer 1, 2026-08-29): the pm slot's DATA client
// defaults to the tenant DB's `pm` schema — the AUTHORITATIVE OPM data since
// the 2026-08-30 write cutover (no longer a synced mirror) — so
// Omni-Projects' legacy `.from('work_items')` reads resolve there. The crm
// slot likewise defaults to the tenant DB's `crm` schema (the table-for-table
// EDNA CRM read mirror; write cutover pending), so OmniCRM's verbatim
// `.from('companies')` / `.from('contacts')` reads resolve there. Auth is
// untouched: still one session, one storage key. Explicit
// `.schema('core')` / `.schema('custom')` calls override per request.
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <OmniAuthProvider
        projects={{
          mail: shellProject,
          crm: { ...shellProject, dbSchema: 'crm' },
          pm: { ...shellProject, dbSchema: 'pm' },
        }}
        shellUrl={window.location.origin}
        // Customer front door (opt-in per instance; unset = exactly as before):
        // a shared session cookie on the parent domain, and PKCE for provider
        // sign-in (Microsoft).
        {...(instance.frontDoor?.cookieDomain
          ? { persistTo: 'cookie' as const, cookieDomain: instance.frontDoor.cookieDomain }
          : {})}
        {...(instance.frontDoor?.providers?.length ? { flowType: 'pkce' as const } : {})}
      >
        <ToastProvider>
          <App />
        </ToastProvider>
      </OmniAuthProvider>
    </QueryClientProvider>
  </StrictMode>,
)
